
import { defineComponent } from "vue";

// Parts
import InCaseCheckBox from "@/views/shared/form-parts/InCaseCheckBox.vue";

// Interfaces
import { SelectPlanFormData } from "@/views/order-form/select-plan/select-plan-interfaces";
import {
  CustomerInfoFormData,
  CustomerInfoFormStatus,
} from "@/views/order-form/customer-info/customer-info-interfaces";
import {
  EstimationData,
  ShireList,
} from "@/views/order-form/order-form-interfaces";

// Tools
import Utility from "@/assets/common/scripts/utility";
import loginUtil from "@/assets/common/scripts/loginUtil";
import { useGlobalStore } from "@/stores/global";

// Constants
import {
  SELECT_PLAN_FORM_DATA_DEFAULT,
  ESTIMATION_DATA_DEFAULT,
} from "@/views/order-form/select-plan/SelectPlan.vue";
import {
  CUSTOMER_INFO_DEFAULT_FORM_DATA,
  CUSTOMER_INFO_FORM_STATUS_DEFAULT,
} from "@/views/order-form/customer-info/CustomerInfo.vue";
import { RECEIVING_METHOD_OPTIONS } from "@/views/order-form/select-plan/component/ReceivingMethod.vue";
import { SEX_OPTIONS } from "@/views/order-form/customer-info/CustomerInfo.vue";

// API
import OrderService, { prepareCreateOrderParams } from "@/api/order.service";
import { AxiosError } from "axios";

export default defineComponent({
  name: "CheckOrder",
  components: { InCaseCheckBox },
  props: {
    selectPlanData: {
      type: Object as () => SelectPlanFormData,
      required: true,
      default: () => {
        return { ...SELECT_PLAN_FORM_DATA_DEFAULT };
      },
    },
    customerInfoData: {
      type: Object as () => CustomerInfoFormData,
      required: true,
      default: () => {
        return { ...CUSTOMER_INFO_DEFAULT_FORM_DATA };
      },
    },
    customerInfoStatus: {
      type: Object as () => CustomerInfoFormStatus,
      required: true,
      default: () => {
        return { ...CUSTOMER_INFO_FORM_STATUS_DEFAULT };
      },
    },
    estimationData: {
      type: Object as () => EstimationData,
      required: true,
      default: () => {
        return { ...ESTIMATION_DATA_DEFAULT };
      },
    },
    prefectures: {
      type: Object as () => ShireList,
      default: () => {
        return {} as ShireList;
      },
    },
    csvOptions: {
      type: Array as () => any[],
      default: () => [],
    },
  },
  computed: {
    globalState() {
      return useGlobalStore();
    },
    isLoggedIn() {
      return this.globalState.$state.loginStatus.isLoggedIn;
    },
    isAna(): boolean {
      return this.selectPlanData.order_shop_code == "ana";
    },
    hasDifferentUser(): boolean {
      return this.customerInfoStatus.hasDifferentUser;
    },
    hasDifferentDestination(): boolean {
      return this.customerInfoStatus.hasDifferentDestination;
    },
    isConditionsChecked(): boolean {
      return (
        this.formStatus.agreeTermsCondition &&
        this.formStatus.agreePrivacyPolicy
      );
    },
    sexLabel(): string {
      switch (this.customerInfoData.order_sex) {
        case SEX_OPTIONS.male.value:
          return SEX_OPTIONS.male.label;
        case SEX_OPTIONS.female.value:
          return SEX_OPTIONS.female.label;
        default:
          return "";
      }
    },
  },
  data() {
    return {
      formStatus: {
        agreeTermsCondition: false,
        agreePrivacyPolicy: false,
        errorMessage: "",
      },
    };
  },
  methods: {
    dateFormat: Utility.dateFormat,
    getPrefNameById(id: string): string {
      if (Utility.isEmptyObject(this.prefectures)) return "";
      const result = Object.values(this.prefectures)
        .map((area) => {
          return area.prefectures;
        })
        .flat(1)
        .find((pref) => pref.shire_id === id);
      return result ? result.shire_name : "";
    },
    calculateDays: Utility.calculateTravelingDays,
    getCountriesList(): { countryName: string; key: string }[] {
      return this.selectPlanData.countries.map((country, index) => {
        return {
          countryName: country,
          key: String(index + 1),
        };
      });
    },
    getReceivingMethodNameByCode(code: string): string {
      const method = Object.values(RECEIVING_METHOD_OPTIONS).find((method) => {
        return method.code === code;
      });
      return method ? method.label : "";
    },
    getMuskedString(text: string): string {
      return text
        .split("")
        .map(() => "*")
        .join("");
    },
    getMuskedCardNumber() {
      return this.customerInfoData.cc.card_number
        .split("")
        .map((elm, index, arr) => {
          if (index < arr.length - 4) return "*";
          return elm;
        })
        .join("");
    },
    getPayMethodName(code: string, shop_type: any): string {
      if (code == "6") {
        let text = this.csvOptions.find((item) => item.value == shop_type);
        return text.label;
      } else if (code == "7") {
        return "法人掛け払い請求書";
      } else {
        return "クレジットカード";
      }
    },
    formatUserData(formData: CustomerInfoFormData): CustomerInfoFormData {
      const hasDifferentUser = this.customerInfoStatus.hasDifferentUser;
      if (!hasDifferentUser) {
        formData.order_use_name = formData.order_name;
        formData.order_use_name2 = formData.order_name2;
        formData.order_use_gnm1 = formData.order_gnm1;
        formData.order_use_gnm2 = formData.order_gnm2;
        return formData;
      }
      return formData;
    },
    formatDeliveryInformation(
      formData: CustomerInfoFormData
    ): CustomerInfoFormData {
      const method = this.selectPlanData.order_get_type;
      const codeDelivery = RECEIVING_METHOD_OPTIONS.delivery.code;
      const hasDifferentDestination =
        this.customerInfoStatus.hasDifferentDestination;
      if (method !== codeDelivery) {
        formData.order_send_name =
          formData.order_send_name2 =
          formData.order_send_gnm1 =
          formData.order_send_gnm2 =
          formData.order_send_postcode =
          formData.order_send_shire_id =
          formData.order_send_address1 =
          formData.order_send_address2 =
          formData.order_send_address3 =
          formData.order_send_company =
          formData.order_send_department =
          formData.order_send_phone =
            "";
        return formData;
      }
      if (!hasDifferentDestination) {
        formData.order_send_name = formData.order_name;
        formData.order_send_name2 = formData.order_name2;
        formData.order_send_gnm1 = formData.order_gnm1;
        formData.order_send_gnm2 = formData.order_gnm2;
        formData.order_send_postcode = formData.order_postcode;
        formData.order_send_shire_id = formData.order_shire_id;
        formData.order_send_address1 = formData.order_address1;
        formData.order_send_address2 = formData.order_address2;
        formData.order_send_address3 = formData.order_address3;
        formData.order_send_company = formData.order_company;
        formData.order_send_department = formData.order_department;
        formData.order_send_phone = formData.order_phone;
        return formData;
      }
      return formData;
    },
    onOrderCheckOut() {
      this.globalState.showLoading();
      const customerInfoData = { ...this.customerInfoData };
      console.log(customerInfoData, "customerInfoData");

      Object.assign(customerInfoData, this.formatUserData(customerInfoData));
      Object.assign(
        customerInfoData,
        this.formatDeliveryInformation(customerInfoData)
      );
      const params = prepareCreateOrderParams(
        this.selectPlanData,
        customerInfoData
      );
      OrderService.createOrder(params)
        .then(({ data: res }) => {
          if (res.result === "9900" && res.errormsg) {
            loginUtil.logout();
            throw new AxiosError(
              "一定時間が経過したため自動的にログアウトされました。プラン選択画面に戻り、再度ログインを行ってください。"
            );
          }
          if (res.result === "9901" && res.errormsg) {
            throw new AxiosError(
              "お客さまのメールアドレスはすでに会員登録済のため、会員登録時のパスワードを入力してください。"
            );
          }
          if ((res.result !== "0000" && res.errormsg) || !res.data) {
            throw new AxiosError(res.errormsg);
          }
          this.$router.push({
            name: "OrderCompleted",
            query: { order_number: res.data.order_no },
          });
        })
        .catch((error) => {
          this.formStatus.errorMessage = error.message;
        })
        .finally(() => {
          this.globalState.hideLoading();
        });
    },
    onBackToCustomerInfo(): void {
      this.$emit("backToCustomerInfo");
    },
  },
  emits: ["backToCustomerInfo"],
});
